import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, MapPin, Users, Clock, BadgeCheck, ArrowLeft, Loader2, Heart } from "lucide-react";
import { BookingDialog } from "@/components/BookingDialog";
import { AuthDialog } from "@/components/AuthDialog";
import { StarRating } from "@/components/StarRating";
import { ServiceCard } from "@/components/ServiceCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isFav, toggleFav } = useAuth();
  const [service, setService] = useState(null);
  const [guests, setGuests] = useState(50);
  const [hours, setHours] = useState(3);
  const [addons, setAddons] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(null);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    setActiveImg(null);
    api.get(`/services/${id}`).then((r) => {
      setService(r.data);
      setGuests(r.data.base_persons);
      setHours(r.data.base_hours);
    });
    api.get(`/services/${id}/availability`).then((r) => setBlocked(r.data.blocked_dates || []));
    api.get(`/services/${id}/reviews`).then((r) => setReviews(r.data));
    api.get(`/services/${id}/similar`).then((r) => setSimilar(r.data));
  }, [id]);

  if (!service) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  const toggleAddon = (i) => setAddons((a) => a.includes(i) ? a.filter((x) => x !== i) : [...a, i]);

  let total = service.base_price;
  total += Math.max(0, guests - service.base_persons) * (service.extra_person_price || 0);
  total += Math.max(0, hours - service.base_hours) * (service.extra_hour_price || 0);
  addons.forEach((i) => { total += service.addons[i]?.price || 0; });
  total = Math.round(total);

  const maxGuests = service.max_persons || service.base_persons * 4;
  const gallery = [service.image_url, ...(service.images || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const fav = isFav(service.id);
  const onFav = async (e) => {
    e.stopPropagation();
    if (!user) { setAuthOpen(true); return; }
    const now = await toggleFav(service.id);
    toast.success(now ? "Zu Favoriten hinzugefügt" : "Aus Favoriten entfernt");
  };

  const startBooking = () => { if (!user) { setAuthOpen(true); } else { setBookOpen(true); } };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-500 hover:text-emerald-700 text-sm mb-5" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm relative">
              <img src={activeImg || gallery[0]} alt={service.title} className="w-full h-72 sm:h-96 object-cover" />
              <button onClick={onFav} data-testid="detail-fav-btn" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/95 flex items-center justify-center shadow hover:scale-110 transition-transform">
                <Heart className={`w-5 h-5 ${fav ? "fill-rose-500 text-rose-500" : "text-slate-600"}`} />
              </button>
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar" data-testid="gallery-thumbs">
                {gallery.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(img)}
                    className={`w-20 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 ${(activeImg || gallery[0]) === img ? "border-emerald-500" : "border-transparent"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3 mt-5 text-sm text-slate-500">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {service.city}</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {service.rating} ({service.reviews} Bewertungen)</span>
              {service.verified && <span className="flex items-center gap-1 text-emerald-700 font-semibold"><BadgeCheck className="w-4 h-4" /> Verifiziert</span>}
            </div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-2">{service.title}</h1>
            <p className="text-slate-500 mt-1">Angeboten von <span className="font-semibold text-slate-700">{service.partner_name}</span></p>
            <p className="text-slate-700 mt-5 leading-relaxed">{service.description}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <Users className="w-5 h-5 mx-auto text-emerald-600 mb-1" /><div className="text-xs text-slate-500">Basis</div><div className="font-bold text-slate-900">{service.base_persons} Pers.</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <Clock className="w-5 h-5 mx-auto text-emerald-600 mb-1" /><div className="text-xs text-slate-500">Dauer</div><div className="font-bold text-slate-900">{service.base_hours} Std</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <MapPin className="w-5 h-5 mx-auto text-emerald-600 mb-1" /><div className="text-xs text-slate-500">Radius</div><div className="font-bold text-slate-900">{service.service_radius_km} km</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <Star className="w-5 h-5 mx-auto text-emerald-600 mb-1" /><div className="text-xs text-slate-500">Bewertung</div><div className="font-bold text-slate-900">{service.rating} / 5</div>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Bewertungen ({reviews.length})</h2>
              {reviews.length === 0 ? (
                <p className="text-slate-500 text-sm" data-testid="no-reviews">Noch keine Bewertungen für dieses Angebot.</p>
              ) : (
                <div className="space-y-4" data-testid="reviews-list">
                  {reviews.map((r) => (
                    <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-800">{r.customer_name}</span>
                        <StarRating value={r.rating} readOnly starClass="w-4 h-4" />
                      </div>
                      {r.comment && <p className="text-slate-600 text-sm mt-2">{r.comment}</p>}
                      {r.reply && (
                        <div className="mt-3 ml-2 pl-3 border-l-2 border-emerald-300 bg-emerald-50/60 rounded-r-lg p-2.5">
                          <div className="text-xs font-semibold text-emerald-700">Antwort von {service.partner_name}</div>
                          <p className="text-sm text-slate-600 mt-0.5">{r.reply}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sticky top-24">
              <div className="font-mono font-extrabold text-3xl text-emerald-700">{euro(total)}</div>
              <p className="text-xs text-slate-500 mb-5">Gesamtpreis für {guests} Personen · {hours} Std</p>

              <div className="mb-5">
                <div className="flex justify-between text-sm font-semibold text-slate-700 mb-2">
                  <span>Personen</span><span className="text-emerald-700">{guests}</span>
                </div>
                <Slider data-testid="guests-slider" value={[guests]} min={service.base_persons} max={maxGuests} step={5} onValueChange={(v) => setGuests(v[0])} />
              </div>
              <div className="mb-5">
                <div className="flex justify-between text-sm font-semibold text-slate-700 mb-2">
                  <span>Stunden</span><span className="text-emerald-700">{hours} Std</span>
                </div>
                <Slider data-testid="hours-slider" value={[hours]} min={service.base_hours} max={service.base_hours + 6} step={1} onValueChange={(v) => setHours(v[0])} />
              </div>

              {service.addons?.length > 0 && (
                <div className="mb-5">
                  <div className="text-sm font-semibold text-slate-700 mb-2">Extras</div>
                  <div className="space-y-2">
                    {service.addons.map((a, i) => (
                      <label key={i} data-testid={`addon-${i}`} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:border-emerald-400">
                        <Checkbox checked={addons.includes(i)} onCheckedChange={() => toggleAddon(i)} />
                        <span className="flex-1 text-sm text-slate-700">{a.name}</span>
                        <span className="text-sm font-semibold text-emerald-700">+{euro(a.price)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={startBooking} data-testid="start-booking-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold rounded-xl">
                Jetzt buchen
              </Button>
              <p className="text-center text-xs text-slate-400 mt-3">Kartenzahlung oder bar am Event</p>
            </div>
          </div>
        </div>
      </div>
      {similar.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-4">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-5">Ähnliche Angebote</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="similar-grid">
            {similar.map((s) => <ServiceCard key={s.id} service={s} />)}
          </div>
        </div>
      )}
      <Footer />
      {bookOpen && <BookingDialog open={bookOpen} onOpenChange={setBookOpen} service={service} guests={guests} hours={hours} addonIndexes={addons} blockedDates={blocked} requireAuth={() => { setBookOpen(false); setAuthOpen(true); }} />}
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => setBookOpen(true)} />
    </div>
  );
}
